import { getActiveEvaluators, isUserFunder } from "@engine/round/accessors";
import type { RoundAggregate } from "@engine/round/aggregate";
import { type UserProfiles } from "@lib/types";
import userIsAdmin from "@client/utils/userIsAdmin";
import userIsSfcAdmin from "@client/utils/userIsSfcAdmin";

/**
 * Where a visit to the bare round URL lands, relative to the round. The one
 * source for the round root's redirect, kept next to the admin pages' own
 * guard (`userIsAdmin`) so the root never sends a viewer to a page that
 * bounces them straight back here.
 *
 * `null` means "no landing yet": the viewer is not a member of the round
 * (the round load already sends non-members away) and has no admin standing.
 */
export function roundLandingPath({
  round,
  userId,
  userProfiles,
  effectiveIsSfcAdmin,
  isRestrictedFunder,
}: {
  round: RoundAggregate;
  userId: string;
  userProfiles: UserProfiles;
  effectiveIsSfcAdmin: boolean;
  isRestrictedFunder: boolean;
}): string | null {
  if (!round.isSfcRound) {
    return "/proposals";
  }

  // SFC admins land on the dashboard even when they are also funders.
  if (userIsSfcAdmin(userId, { userProfiles, effectiveIsSfcAdmin })) {
    return "/admin/dashboard";
  }

  // Other funders land in their workspace first.
  if (isUserFunder(round, userId)) {
    return isRestrictedFunder ? "/funder/decide" : "/funder/review";
  }

  const member = round.members[userId];
  const isRoundAdmin = member ? member.isRoundAdmin : effectiveIsSfcAdmin;

  if (isRoundAdmin) {
    return "/admin/dashboard";
  }

  const memberTrack = member?.track;

  if (memberTrack == null) {
    return null;
  }

  // A member without a track has no phase to follow. An SFC admin among them
  // gets the admin landing; anyone else — one just added, before an admin
  // assigns their track — must not, because the Round Dashboard applies this
  // same test on arrival and turns non-admins back to the round root, so the
  // two redirects would chase each other forever. They land where every
  // member can look around.
  if (memberTrack === "NO_TRACK") {
    return userIsAdmin(userId, { round, userProfiles, effectiveIsSfcAdmin })
      ? "/admin/dashboard"
      : "/proposals";
  }

  switch (round.trackSettings[memberTrack].status) {
    case "1.onboarding":
      return "/onboarding";

    case "2.initial-evaluations": {
      const evaluator = getActiveEvaluators(round).find(
        (evaluator) => evaluator.memberId === userId,
      );
      return evaluator ? "/proposals" : "/investigations";
    }

    case "3.full-discussion":
    case "4.evals-freeze":
    case "5.complete":
      return "/proposals";
  }
}
